import { TagKey } from '@cof/tag-catalog-fs-vehicle-and-loan-mfe';
import { DebugService } from '../shared/services/debug.service.js';
import { REFI_VEHICLE_LOAN_CONST } from '../shared/constants/app.constants.js';
import { stratum } from '../../constants/stratum.js';

class _ApplicationService {
  constructor() {
    this.debug = new DebugService('ApplicationService');
    this._application = {};
    this._marketingOffer = {};
    this._isUpfrontOffers = {};
    this._experiments = [];
    this._vehicleAndLoanMatchList = [];
    this.abTest = null;
    this._abTests = {
      name: 'v&l-ab-tests',
      variationIds: [],
      testGroup: '',
      testWeight: 1,
    };
    this._isIconicV2 = false;
    this._isInAppDeclineDelay = false;
    this._hasConsent = false;
    this._isInvisiblePQ = false;
  }

  get Application() {
    return this._application;
  }

  setApplication(application) {
    this._application = application;
  }

  get applicant() {
    return this._application?.primaryApplicant;
  }

  get isJointApplication() {
    return (
      this._application?.applicationType ===
      REFI_VEHICLE_LOAN_CONST.APPLICATION_TYPE.JOINT
    );
  }

  get MarketingOffer() {
    return this._marketingOffer;
  }

  set MarketingOffer(marketingOffer) {
    this._marketingOffer = marketingOffer;
  }

  set selectedVehicle(selectedVehicle) {
    const selectedVehicleForApplication = {
      make: selectedVehicle.make,
      model: selectedVehicle.model,
      trim: selectedVehicle.trim,
      year: selectedVehicle.year,
      vin: selectedVehicle.vin,
      wholeSaleValue: selectedVehicle.wholeSaleValue,
    };
    selectedVehicleForApplication.state =
      this.applicant?.currentAddress?.stateCode;
    this.debug.log(
      'updating application with selected vehicle',
      selectedVehicleForApplication
    );
    this._application.selectedVehicle = selectedVehicleForApplication;
    this.checkVehicleAndLoanMatchSelection();
  }

  setSelectedLoan(useManualLoan, selectedLoan) {
    this.debug.log('updating application with selected loan', selectedLoan);
    this._application.manualEntryFlag = useManualLoan;
    this._application.selectedLoan = selectedLoan;
    this._application.paymentDueDay = selectedLoan.paymentDueDay;
    this.checkVehicleAndLoanMatchSelection();
  }

  checkVehicleAndLoanMatchSelection() {
    this._application.vehicleAndLoanMatchSelected = false;
    this.debug.log('vehicleAndLoanMatchSelected set to false');

    if (
      !this._application.selectedLoan === false &&
      !this._application.selectedVehicle === false
    ) {
      const currentApp = this._application;

      if (!this.validateVehicleAndLoan()) {
        return;
      }

      for (const valMatch of this._vehicleAndLoanMatchList) {
        if (
          valMatch.vehicle !== null &&
          valMatch.loan !== null &&
          valMatch.vehicle.vin === currentApp.selectedVehicle.vin &&
          valMatch.loan.lenderName === currentApp.selectedLoan.lenderName &&
          valMatch.loan.apr === currentApp.selectedLoan.apr &&
          valMatch.loan.monthlyPayment ===
            currentApp.selectedLoan.monthlyPayment
        ) {
          this.setVALMatchFlag();
          return;
        }
      }
      stratum.publishTag(TagKey.VEHICLE_LOAN_MATCH_VEHICLE_LOAN_MATCH_FLAG, {
        replacements: {
          value: 'False - No VAL match or VAL match not selected',
        },
      });
    }
  }

  validateVehicleAndLoan() {
    if (
      this._application.selectedVehicle.vin == null ||
      this._application.selectedLoan.lenderName == null ||
      this._application.selectedLoan.apr == null ||
      this._application.selectedLoan.monthlyPayment == null
    ) {
      this.debug.log('One or more values are null or undefined');
      stratum.publishTag(TagKey.VEHICLE_LOAN_MATCH_VEHICLE_LOAN_MATCH_FLAG, {
        replacements: {
          value: 'False - No VAL match or VAL match not selected',
        },
      });
      return false;
    }
    if (
      this._application.selectedLoan.lenderName.toLowerCase().endsWith('cu') ||
      this._application.selectedLoan.lenderName.toLowerCase().includes('fcu') ||
      this._application.selectedLoan.lenderName
        .toLowerCase()
        .includes('credit union')
    ) {
      this.debug.log("Lender name contains '%credit union%', '%fcu%', '%cu'");
      stratum.publishTag(TagKey.VEHICLE_LOAN_MATCH_VEHICLE_LOAN_MATCH_FLAG, {
        replacements: {
          value: 'False - Lender is Credit Union',
        },
      });
      return false;
    }
    return true;
  }

  setVALMatchFlag() {
    if (this._experiments.includes(REFI_VEHICLE_LOAN_CONST.VAL_MATCH_TEST)) {
      this._application.vehicleAndLoanMatchSelected = true;
      this.debug.log('vehicleAndLoanMatchSelected set to true');
      stratum.publishTag(TagKey.VEHICLE_LOAN_MATCH_VEHICLE_LOAN_MATCH_FLAG, {
        replacements: {
          value: 'True - VAL match selected',
        },
      });
    } else {
      this.debug.log(
        'vehicleAndLoanMatchSelected set to false due to VAL Interview Experiment'
      );
    }
  }

  get selectedLoan() {
    return this._application.selectedLoan;
  }

  set isUpfrontOffers(upfrontOffers) {
    this._isUpfrontOffers = upfrontOffers;
  }

  get isUpfrontOffers() {
    return this._isUpfrontOffers;
  }

  get isIconicAndToggledOn() {
    return this._isIconicAndToggledOn;
  }

  set isIconicAndToggledOn(isIconicAndToggledOn) {
    this._isIconicAndToggledOn = isIconicAndToggledOn;
  }

  get isInvisiblePQ() {
    return this._isInvisiblePQ;
  }

  set isInvisiblePQ(isInvisiblePQ) {
    this._isInvisiblePQ = isInvisiblePQ;
  }

  updateInvisiblePQ() {
    this.debug.log(
      'updating invisible PQ, this.experiments is ',
      this._experiments
    );
    this._isInvisiblePQ = this._experiments?.includes(
      REFI_VEHICLE_LOAN_CONST.ICONIC_INVISIBLE_PQ
    );
  }

  get isIconicV2() {
    return this._isIconicV2;
  }

  set isIconicV2(isIconicV2) {
    this._isIconicV2 = isIconicV2;
  }

  updateIconicV2() {
    this.debug.log(
      'updating iconic v2 toggle, this.experiments is ',
      this._experiments
    );
    this._isIconicV2 = this._experiments?.includes(
      REFI_VEHICLE_LOAN_CONST.ICONIC_V2
    );
  }

  get isInAppDeclineDelayOn() {
    return this._isInAppDeclineDelay;
  }

  set hasConsent(hasConsent) {
    this._hasConsent = hasConsent;
  }

  get hasConsent() {
    return this._hasConsent;
  }

  updateInAppDeclineDelayToggle() {
    if (
      this._experiments?.includes(
        REFI_VEHICLE_LOAN_CONST.IN_APP_DECLINE_DELAY_TEST
      )
    ) {
      this._isInAppDeclineDelay = true;
    }
  }

  get experiments() {
    return this._experiments;
  }

  updateExperiments(newExperiments, removeExperiments = []) {
    stratum.endAbTest(this.abTest);
    const newlyAddedExperiments = newExperiments.map((experiment) =>
      experiment.toLowerCase()
    );
    const updatedExperiments = this._experiments.filter(
      (experiment) => !removeExperiments.includes(experiment)
    );
    this._experiments = Array.from(
      new Set(updatedExperiments.concat(newlyAddedExperiments))
    );
    this._abTests.variationIds = this._experiments;
    this.abTest = stratum.startAbTest(this._abTests);
  }
}
export const ApplicationService = new _ApplicationService();
